import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Icon from "@/components/Icon";
import AdminLayout from "@/components/AdminLayout";

const PLAN_KEYS = ["basic", "advanced", "professional"];
const inputCls = "mt-1 w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm disabled:opacity-60";

function LimitInput({ label, value, onChange, disabled, testid, min = 0 }) {
  const unlimited = value === null || value === undefined;
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground">{label}</label>
      <div className="flex items-center gap-2">
        <input data-testid={testid} type="number" min={min} disabled={disabled || unlimited} value={unlimited ? "" : value} placeholder={unlimited ? "Unlimited" : ""}
          onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))} className={inputCls} />
        <label className="flex items-center gap-1 text-xs whitespace-nowrap mt-1"><input data-testid={`${testid}-unlimited`} type="checkbox" disabled={disabled} checked={unlimited} onChange={(e) => onChange(e.target.checked ? null : min || 1)} />Unlimited</label>
      </div>
    </div>
  );
}

function PlanCard({ p, plan, cat, set, disabled }) {
  const setF = (k, v) => set({ ...plan, features: { ...plan.features, [k]: v } });
  const setAI = (k, v) => set({ ...plan, ai_allowance: { ...plan.ai_allowance, [k]: v } });
  return (
    <div data-testid={`plan-card-${p}`} className="rounded-2xl border border-border bg-card p-5 space-y-3">
      <div className="text-xs font-bold uppercase tracking-wider text-primary">{p}</div>
      <div><label className="text-xs font-semibold text-muted-foreground">Display name</label><input data-testid={`plan-${p}-label`} disabled={disabled} value={plan.label} onChange={(e) => set({ ...plan, label: e.target.value })} className={inputCls} /></div>
      <LimitInput label="Total users (incl. owner)" testid={`plan-${p}-users`} min={1} value={plan.users} disabled={disabled} onChange={(v) => set({ ...plan, users: v })} />
      <LimitInput label="QR code limit" testid={`plan-${p}-qr`} value={plan.qr} disabled={disabled} onChange={(v) => set({ ...plan, qr: v })} />
      <div><label className="text-xs font-semibold text-muted-foreground">Custom domains per company</label><input data-testid={`plan-${p}-custom-domains`} type="number" min={0} max={10} disabled={disabled} value={plan.custom_domains ?? 0} onChange={(e) => set({ ...plan, custom_domains: Number(e.target.value) })} className={inputCls} /></div>
      <div><label className="text-xs font-semibold text-muted-foreground">Display price ($/mo, informational — Stripe unchanged)</label><input data-testid={`plan-${p}-price`} type="number" disabled={disabled} value={plan.price} onChange={(e) => set({ ...plan, price: Number(e.target.value) })} className={inputCls} /></div>
      <label className="flex items-center gap-2 text-sm"><input data-testid={`plan-${p}-watermark`} type="checkbox" disabled={disabled} checked={!!plan.watermark} onChange={(e) => set({ ...plan, watermark: e.target.checked })} />Watermark QR codes</label>
      <div className="pt-2 border-t border-border">
        <div className="text-xs font-bold mb-1.5">Features</div>
        {Object.entries(cat.features).map(([k, label]) => {
          const na = cat.unavailable[k];
          const gk = cat.global_map[k];
          const gOff = gk && cat.global_flags[gk] === false;
          return (
            <label key={k} className={`flex items-center gap-2 text-sm py-0.5 ${na ? "opacity-50" : ""}`}>
              <input data-testid={`plan-${p}-feature-${k}`} type="checkbox" disabled={disabled || !!na} checked={!na && plan.features?.[k] !== false} onChange={(e) => setF(k, e.target.checked)} />
              {label}{na && <span className="text-[10px] text-muted-foreground">({na})</span>}{gOff && <span className="text-[10px] text-red-600">(globally off)</span>}
            </label>
          );
        })}
      </div>
      <div className="pt-2 border-t border-border space-y-2">
        <div className="text-xs font-bold">AI allowance per company / period</div>
        {cat.ai_features.map((k) => <LimitInput key={k} label={cat.features[k]} testid={`plan-${p}-ai-${k}`} value={plan.ai_allowance?.[k]} disabled={disabled} onChange={(v) => setAI(k, v)} />)}
      </div>
    </div>
  );
}

function Impact({ impact }) {
  if (!impact) return null;
  return (
    <div data-testid="plans-impact" className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-5 text-sm space-y-2">
      <div className="font-bold">Impact preview</div>
      {impact.impact.length === 0 ? <p>No changes detected.</p> : impact.impact.map((r) => (
        <div key={r.plan}>
          <b className="capitalize">{r.plan}</b> — {r.companies_on_plan} compan{r.companies_on_plan === 1 ? "y" : "ies"} on this plan. Changes: {r.changes.map((c) => `${c.field}${c.before !== undefined ? ` ${c.before ?? "∞"}→${c.after ?? "∞"}` : `: ${JSON.stringify(c.after)}`}`).join(", ")}
          {r.over_seat_limit.length > 0 && <div className="text-red-600">Over new seat limit (kept, flagged, owner notified): {r.over_seat_limit.map((c) => `${c.name} (${c.active})`).join(", ")}</div>}
          {r.over_qr_limit.length > 0 && <div className="text-red-600">Over new QR limit (existing QR codes keep working; new creation blocked): {r.over_qr_limit.map((c) => `${c.name} (${c.qr})`).join(", ")}</div>}
        </div>
      ))}
      <p className="text-xs text-muted-foreground">{impact.note}</p>
    </div>
  );
}

export default function AdminPlans() {
  const [plans, setPlans] = useState(null);
  const [cat, setCat] = useState(null);
  const [versions, setVersions] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [impact, setImpact] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/plans").then(({ data }) => { setPlans(data.plans); setVersions(data.versions || []); }).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/plans/catalog").then(({ data }) => setCat(data)).catch(() => {});
    api.get("/admin/me").then(({ data }) => setIsOwner(data.platform_role === "owner")).catch(() => {});
  }, []);
  const payload = () => Object.fromEntries(PLAN_KEYS.map((p) => [p, { ...plans[p], qr: plans[p].qr, users: plans[p].users }]));
  const preview = async () => { try { const { data } = await api.post("/admin/plans/impact", { plans: payload() }); setImpact(data); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const save = async () => {
    if (!impact) return toast.error("Preview the impact first");
    if (reason.trim().length < 3) return toast.error("Add a reason for this change");
    setSaving(true);
    try { const { data } = await api.put("/admin/plans", { plans: payload(), reason }); setPlans(data.plans); setImpact(null); setReason(""); toast.success(`Plans saved${data.over_seat_limit?.length ? ` — ${data.over_seat_limit.length} company(ies) flagged over limit` : ""}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <AdminLayout title="Plans & Features" subtitle="Seat, QR and AI limits plus feature access per plan. Enforced by the backend; Stripe prices are never changed here.">
      {!plans || !cat ? <div className="grid place-items-center py-20"><Icon name="Loader2" className="h-7 w-7 animate-spin text-primary" /></div> : (
        <div className="space-y-6" data-testid="admin-plans">
          {!isOwner && <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-600 px-4 py-3 text-sm">Viewing as Admin — only the platform Owner can edit plans.</div>}
          <div className="rounded-xl border border-border bg-card px-4 py-3 text-xs text-muted-foreground">QR type access per plan is set in <b>QR Types</b>. Global AI/menu shutdowns are set in <b>Restaurant Menus → Settings</b> and override every plan and company override.</div>
          <div className="grid lg:grid-cols-3 gap-4">{PLAN_KEYS.map((p) => <PlanCard key={p} p={p} plan={plans[p]} cat={cat} disabled={!isOwner} set={(v) => { setImpact(null); setPlans({ ...plans, [p]: v }); }} />)}</div>
          <Impact impact={impact} />
          {isOwner && (
            <div className="rounded-2xl border border-border bg-card p-5 flex flex-wrap items-center gap-3">
              <input data-testid="plans-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for this change (required)" className="flex-1 min-w-[240px] rounded-lg border border-input bg-background px-3 py-2 text-sm" />
              <button data-testid="plans-preview" onClick={preview} className="rounded-xl border border-border px-4 py-2 text-sm font-semibold hover:bg-accent">Preview impact</button>
              <button data-testid="save-plans" onClick={save} disabled={saving || !impact} className="rounded-xl bg-primary text-primary-foreground px-5 py-2 text-sm font-bold disabled:opacity-50">{saving ? "Saving..." : "Save changes"}</button>
            </div>
          )}
          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="text-sm font-bold mb-3">Change history</h3>
            {versions.length === 0 ? <p className="text-sm text-muted-foreground">No changes recorded yet.</p> : versions.map((v) => (
              <div key={v.id} className="flex flex-wrap justify-between gap-2 text-sm py-1.5 border-b border-border/60 last:border-0">
                <span className="text-muted-foreground">{new Date(v.created_at).toLocaleString()}</span><span className="flex-1 truncate px-2">{v.reason}</span><span className="font-medium">{v.changed_by}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
